import React, { useMemo, useRef } from 'react'
import { useDrop } from 'react-dnd'
import Cell from './Cell'
import { useStore } from '../../store/store'
import { MASKS_TYPE } from '../../constants/constants'
import { useStyleMap } from '../../hooks'
import { computeMergedCellLayout, mergedCellSpanKey, originalColumnLabel } from '../../utils/table'
import { cellStyleEntry, rowStyleCss } from '../../shared/tables/tableRowStyle'

const PROPOSAL_TABLE_ROW_TYPE = 'proposal-table-row'

let currentDragHoverRowId = null

const clearDragRowClasses = () => {
  document.querySelectorAll('[data-drag-row-cell].row-line-hovered').forEach(el => {
    el.classList.remove('row-line-hovered', 'is-first-row')
  })
}

const TableBodyRow = ({ rowId, moveRow, isActive, children }) => {
  const rowRef = useRef(null)

  const [, dropRef] = useDrop(
    () => ({
      accept: PROPOSAL_TABLE_ROW_TYPE,
      hover: (_item, monitor) => {
        if (!monitor.isOver({ shallow: true })) return
        if (currentDragHoverRowId === rowId) return
        clearDragRowClasses()
        currentDragHoverRowId = rowId
        document.querySelectorAll(`[data-drag-row-cell="${rowId}"]`).forEach(el => {
          el.classList.add('row-line-hovered', 'is-first-row')
        })
      },
      drop: ({ id: draggedId }) => {
        if (draggedId !== rowId && moveRow) {
          moveRow(draggedId, rowId)
        }
        clearDragRowClasses()
        currentDragHoverRowId = null
      },
    }),
    [rowId, moveRow]
  )

  return (
    <tr
      ref={(el) => {
        rowRef.current = el
        if (isActive) dropRef(el)
        else dropRef(null)
      }}
      style={{ transform: 'translate(0, 0)' }}
    >
      {children}
    </tr>
  )
}

const TableBody = ({
  table,
  columns,
  isSelected,
  boxId,
  handleClickInTable,
  tableStyle,
  theadHeight,
  data,
  convertAlign,
  convertVerticalAlign,
  computeMaxColumnWidth,
  setColumnSizeVars,
  rotation,
  drawMode,
  box,
  controlRowsClicked,
  setControlRowsClicked,
  verticalAlign,
  moveRow,
  onControlRowClick,
  mergedCells,
  rowStylesByKey,
  selectionRect
}) => {
  const styleMap = useStyleMap()

  const mergedLayout = useMemo(
    () => computeMergedCellLayout(data, columns, mergedCells),
    [data, columns, mergedCells]
  )

  // Bornes de sélection normalisées (l'ancre peut être après le focus).
  const selBounds = useMemo(() => {
    if (!selectionRect) return null
    return {
      r0: Math.min(selectionRect.r0, selectionRect.r1),
      r1: Math.max(selectionRect.r0, selectionRect.r1),
      c0: Math.min(selectionRect.c0, selectionRect.c1),
      c1: Math.max(selectionRect.c0, selectionRect.c1),
    }
  }, [selectionRect])

  const updateBox = useStore(({ updateBox }) => updateBox)
  const setEventManagerEnabled = useStore(({ setEventManagerEnabled }) => setEventManagerEnabled)
  const fromPdf = useStore(({ fromPdf }) => fromPdf)
  const currentDocument = useStore(({ currentDocument }) => currentDocument)
  const currentProposal = useStore(({ currentProposal }) => currentProposal)
  const landscape = useStore(({ landscape }) => landscape)
  const header = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.HEADER.id))
  const footer = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.FOOTER.id))
  const configuration = useStore(({ configuration }) => configuration)

  const getRowSpan = (rowIdx, colKey) => {
    let count = 1
    for (let i = rowIdx + 1; i < data.length; i++) {
      if (data[i].hiddenColumnsKeys?.includes(colKey)) {
        count++
      } else {
        break
      }
    }
    return count
  }

  const renderCell = (cell, rowIdx) => {
    const colIdx = columns.findIndex((col) => col.key === cell.column.id)
    const col = columns[colIdx]
    const nextColKey = colIdx !== columns.length - 1 ? columns[colIdx + 1].key : undefined
    const row = data[rowIdx]
    const nextRowKey = rowIdx !== data.length - 1 ? data[rowIdx + 1].key : undefined
    const cellKey = mergedCellSpanKey(row.key, col.key)
    const mergeSpan = mergedLayout.spanByCell.get(cellKey)
    const isCovered = mergedLayout.coveredCells.has(cellKey)
    // Fusion manuelle prioritaire ; sinon fusion verticale héritée (hiddenColumnsKeys).
    const rowSpan = mergeSpan ? mergeSpan.rowSpan : getRowSpan(rowIdx, col.key)
    const colSpan = mergeSpan ? mergeSpan.colSpan : 1
    // Une ancre fusionnée jusqu'à la dernière colonne doit porter la bordure
    // droite (la vraie dernière colonne est masquée -> sinon bordure absente).
    const spansToLastColumn = colIdx + colSpan - 1 === columns.length - 1
    // Fusion de plusieurs lignes jusqu'à la dernière colonne : sa poignée est
    // la seule de ces lignes, et les représente toutes (cf. rowGroupKeys).
    const groupRowSpan = spansToLastColumn && rowSpan > 1 ? rowSpan : 1
    const hidden = isCovered || data[rowIdx].hiddenColumnsKeys.includes(cell.column.id)
    const isCellSelected = !!selBounds &&
      rowIdx >= selBounds.r0 && rowIdx <= selBounds.r1 &&
      colIdx >= selBounds.c0 && colIdx <= selBounds.c1
    // Une ancre fusionnée dont la cellule est vide porte le contenu d'une autre
    // colonne de la fusion (libellé ramené à gauche) : elle en prend aussi la
    // clé, la valeur et l'alignement, pour qu'une saisie aille à la bonne cellule.
    const contentCol = (mergeSpan?.contentColumnKey &&
      columns.find((c) => c.key === mergeSpan.contentColumnKey)) || col
    // Style de la ligne (configuration du tableau). Un alignement posé sur la
    // case passe avant le sien, le sien avant celui de la colonne.
    const rowRule = rowStylesByKey?.get(row.key)
    // Réglages propres à cette colonne de la ligne, par-dessus ceux de la ligne :
    // par libellé d'origine dans un tableau variable (ses clés changent à chaque
    // lecture), par clé dans un tableau custom (son intitulé se renomme).
    const cellEntry = cellStyleEntry(rowRule, box.tableName ? originalColumnLabel(contentCol) : contentCol.key)
    const align = row[`textAlign_${contentCol.key}`] ?? cellEntry?.align ?? rowRule?.align ?? contentCol.textAlign ?? 'start'
    const rowVerticalAlign = row[`verticalAlign_${contentCol.key}`] ?? contentCol.verticalAlign ?? 'center'
    const rowCss = rowStyleCss(rowRule, rowStylesByKey?.get(nextRowKey), {
      isFirstColumn: colIdx === 0,
      isLastColumn: spansToLastColumn,
    })
    const cellCss = rowStyleCss(cellEntry)
    const cellRowStyle = cellCss ? { ...rowCss, ...cellCss } : rowCss
    return (
      <Cell
        hidden={hidden}
        key={cell.id}
        rowSpan={rowSpan}
        colSpan={colSpan}
        isCellSelected={isCellSelected}
        rowIndex={rowIdx}
        colIndex={colIdx}
        box={box}
        handleClickInTable={handleClickInTable}
        type={contentCol.type ?? 'text'}
        cellContent={row[contentCol.key]}
        tableIsSelected={isSelected}
        boxId={boxId}
        columnKey={contentCol.key}
        rowKey={row.key}
        theadHeight={theadHeight}
        tableStyle={tableStyle}
        align={convertAlign(align)}
        verticalAlign={convertVerticalAlign(rowVerticalAlign)}
        isEven={rowIdx % 2 === 0}
        computeMaxColumnWidth={computeMaxColumnWidth}
        setColumnSizeVars={setColumnSizeVars}
        rotation={rotation}
        isLastColumn={spansToLastColumn}
        isFirstRow={rowIdx === 0}
        isLastRow={rowIdx + groupRowSpan - 1 === data.length - 1}
        isFirstColumn={colIdx === 0}
        drawMode={drawMode}
        controlRowsClicked={controlRowsClicked}
        setControlRowsClicked={setControlRowsClicked}
        nextRowKey={nextRowKey}
        zIndex={columns.length - colIdx + 101}
        placementIndex={rowIdx + groupRowSpan}
        groupRowSpan={groupRowSpan}
        nextColKey={nextColKey}
        moveRow={moveRow}
        onControlRowClick={onControlRowClick}
        configuration={configuration}
        currentDocument={currentDocument}
        currentProposal={currentProposal}
        landscape={landscape}
        header={header}
        footer={footer}
        updateBox={updateBox}
        setEventManagerEnabled={setEventManagerEnabled}
        fromPdf={fromPdf}
        styleMap={styleMap}
        rowStyle={cellRowStyle}
      />
    )
  }

  return (
    <tbody>
      {table.getRowModel().rows.map((row, idx) => (
        <TableBodyRow key={row.id} rowId={row.id} moveRow={moveRow} isActive={isSelected}>
          {row.getVisibleCells().map((cell) => {
            return renderCell(cell, idx)
          })}
        </TableBodyRow>
      ))}
    </tbody>
  )
}

const MemoizedTableBody = React.memo(
  TableBody,
  (prev, next) => {
    return prev.data === next.data &&
      prev.columns.length === next.columns.length &&
      prev.tableStyle === next.tableStyle &&
      prev.theadHeight === next.theadHeight &&
      prev.controlRowsClicked === next.controlRowsClicked &&
      prev.moveRow === next.moveRow &&
      prev.onControlRowClick === next.onControlRowClick &&
      prev.isSelected === next.isSelected &&
      prev.mergedCells === next.mergedCells &&
      prev.rowStylesByKey === next.rowStylesByKey &&
      prev.selectionRect === next.selectionRect &&
      prev.rotation === next.rotation
  }
)

export default MemoizedTableBody
